import { useState } from 'react';
import { WEIGHT_LOSS_PERCENT } from '@oncocoach/care';
import type { AppSession, PatientEvent, PatientView } from '../../lib/types';
import { ApiError, api } from '../../lib/api';
import { fmtAgo, fmtInt } from '../../lib/format';
import { Icon } from '../../components/Mark';

export type VitalKind = 'weight' | 'intake' | 'bowel' | 'steps';

const SPEC = {
  weight: { metric: 'weight-lbs', title: 'Log your weight', unit: 'lbs', placeholder: '148.4', hint: 'Same time each morning, after the bathroom and before breakfast.' },
  intake: { metric: 'intake-ml', title: "Log today's fluids", unit: 'ml', placeholder: '1,600', hint: 'Everything wet counts: tea, squash, soup, ice lollies. Enter the running total for today.' },
  bowel: { metric: 'bowel-movements', title: 'Bowel movements today', unit: 'today', placeholder: '3', hint: 'The total so far today, and whether that is more than usual for you.' },
  steps: { metric: null, title: "Log today's activity", unit: 'steps', placeholder: '3,200', hint: 'Short and often counts. Ten minutes after a meal is a real walk.' },
} as const;

/**
 * The numbers a patient enters themselves.
 *
 * Fluids and bowel movements are entered as a RUNNING TOTAL for the day rather than one entry per
 * event, because nobody opens an app six times to log a drink. The state keeps the largest number
 * of the day, so topping it up later never makes the day smaller.
 */
export function VitalLogFlow({ session, view, kind, onDone, onClose }: { session: AppSession; view: PatientView; kind: VitalKind; onDone: () => Promise<void>; onClose: () => void }) {
  const spec = SPEC[kind];
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ message: string | null } | null>(null);

  const n = Number(value.replace(/,/g, ''));
  const valid = value.trim() !== '' && Number.isFinite(n) && n >= 0 && (kind === 'weight' || Number.isInteger(n));
  const last = spec.metric ? view.metrics[spec.metric] : null;
  const stepGoal = view.progress.steps.todayGoal;

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const event: PatientEvent =
        kind === 'steps'
          ? { type: 'steps', count: Math.round(n), source: 'manual' }
          : { type: 'metric', kind: spec.metric as 'weight-lbs' | 'intake-ml' | 'bowel-movements', value: kind === 'weight' ? Math.round(n * 10) / 10 : Math.round(n), source: 'manual' };
      const r = await api.postEvent(event, session.token);
      setDone({ message: messageFor(kind, r.view) });
      await onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label={spec.title}>
      <div className="modal">
        <header className="modal-head">
          <h2>{done ? 'Logged' : spec.title}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            <Icon name="close" size={18} />
          </button>
        </header>

        <div className="modal-body">
          {done ? (
            <p>{done.message ?? 'Saved.'}</p>
          ) : (
            <>
              {last ? (
                <p className="hint">
                  Last: {kind === 'weight' ? last.value.toFixed(1) : fmtInt(last.value)} {spec.unit} · {fmtAgo(last.at)}
                </p>
              ) : (
                <p className="hint">{spec.hint}</p>
              )}
              {kind === 'steps' && stepGoal ? (
                <p className="hint">
                  Today so far: {fmtInt(view.stepsToday)} of {fmtInt(stepGoal)}.
                </p>
              ) : null}
              <label className="field">
                <span>
                  {spec.title} ({spec.unit})
                </span>
                <input inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} placeholder={spec.placeholder} autoFocus />
              </label>
              {error ? <p className="form-error">{error}</p> : null}
            </>
          )}
        </div>

        <footer className="modal-foot">
          {done ? (
            <button type="button" className="primary" onClick={onClose}>
              Done
            </button>
          ) : (
            <button type="button" className="primary big" disabled={!valid || busy} onClick={() => void submit()}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

/** What to say back. From the view the server returned, so it is never a guess about what was saved. */
function messageFor(kind: VitalKind, view: PatientView): string | null {
  if (kind === 'weight') {
    const w = view.weight;
    if (!w) return 'Weight saved.';
    if (w.lossPercent !== null && w.lossPercent >= WEIGHT_LOSS_PERCENT) {
      return `${w.latest} lb — that is ${w.lossPercent}% down over ${w.overDays} days. Your team can help with this; tell them at your next contact.`;
    }
    return `${w.latest} lb saved. Keeping your weight up is half of getting through treatment well.`;
  }
  if (kind === 'intake') {
    const today = view.intakeToday;
    const goal = view.goals.find((g) => g.kind === 'intake');
    if (today === null) return 'Saved.';
    if (goal && goal.kind === 'intake' && today >= goal.mlPerDay) return `${fmtInt(today)} ml today — that is your target reached.`;
    return `${fmtInt(today)} ml today${goal && goal.kind === 'intake' ? ` of ${fmtInt(goal.mlPerDay)} ml` : ''}. Small sips, often.`;
  }
  if (kind === 'bowel') {
    const today = view.bowelToday;
    return today === null ? 'Saved.' : `${fmtInt(today)} today. If that is more than usual for you, tell your team — do not wait to see.`;
  }
  const goal = view.progress.steps.todayGoal;
  if (goal && view.stepsToday >= goal) return `${fmtInt(view.stepsToday)} steps — goal reached.`;
  return `${fmtInt(view.stepsToday)} steps today${goal ? ` of ${fmtInt(goal)}` : ''}.`;
}
